import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../services/help';
import { apiKeyService } from '../../services';
import { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import SectionWrapper from '../SectionWrapper';

// Minimalistic card style (Google-style) - gleich wie DashboardSection
const minimalCard = {
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  borderRadius: '8px',
  border: '1px solid rgb(0 0 0 / 0.05)',
  transition: 'box-shadow 150ms ease',
  '&:hover': {
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
  }
};

const ApiKeysPage: React.FC = () => {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog für neuen API-Key
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [expirationDays, setExpirationDays] = useState<number | ''>('');
  const [creating, setCreating] = useState(false);
  
  // Neu erstellter Key (wird nur einmal angezeigt)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{
    key: string;
    name: string;
    expiresAt: string | null;
  } | null>(null);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [keyWasCopied, setKeyWasCopied] = useState(false);
  
  // Success/Error Messages
  const { showNotification } = useNotification();

  // Lade API-Keys
  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const keys = await apiKeyService.getApiKeys();
      setApiKeys(keys);
      setError(null);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError(t('settings:messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  // Erstelle neuen API-Key
  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      showNotification(t('settings:createDialog.enterName'), 'error');
      return;
    }

    try {
      setCreating(true);
      const request: CreateApiKeyRequest = {
        name: newKeyName.trim(),
        expiresInDays: expirationDays || undefined,
      };

      const response: CreateApiKeyResponse = await apiKeyService.createApiKey(request);
      
      if (response.status === 'success' && response.data) {
        // Zeige den neuen Key nur einmal an
        setNewlyCreatedKey({
          key: response.data.key,
          name: response.data.name,
          expiresAt: response.data.expiresAt
        });
        setShowNewKeyDialog(true);
        
        // Dialog schließen und Felder zurücksetzen
        setCreateDialogOpen(false);
        setNewKeyName('');
        setExpirationDays('');
        
        // API-Keys neu laden
        await loadApiKeys();
        
        showNotification(t('settings:createDialog.success'), 'success');
      } else {
        throw new Error(response.message || t('settings:createDialog.error'));
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      showNotification(err instanceof Error ? err.message : t('settings:createDialog.error'), 'error');
    } finally {
      setCreating(false);
    }
  };

  // Kopiere neuen Key in Zwischenablage
  const handleCopyNewKey = async () => {
    if (!newlyCreatedKey) return;
    
    try {
      await navigator.clipboard.writeText(newlyCreatedKey.key);
      setKeyWasCopied(true);
      showNotification(t('settings:messages.keyCopied'), 'success');
    } catch (err) {
      console.error('Error copying:', err);
      showNotification(t('settings:messages.copyError'), 'error');
    }
  };

  // Schließe New-Key-Dialog
  const handleCloseNewKeyDialog = () => {
    setShowNewKeyDialog(false);
    setNewlyCreatedKey(null);
    setKeyWasCopied(false);
  };

  // Lösche API-Key
  const handleDeleteKey = async (keyId: string, keyName: string) => {
    if (!window.confirm(t('settings:deleteDialog.message', { name: keyName }))) {
      return;
    }

    try {
      const response = await apiKeyService.deleteApiKey(keyId);
      if (response.status === 'success') {
        await loadApiKeys();
        showNotification(t('settings:deleteDialog.success', { name: keyName }), 'success');
      } else {
        throw new Error(response.message || t('settings:deleteDialog.error'));
      }
    } catch (err) {
      console.error('Error deleting API key:', err);
      showNotification(err instanceof Error ? err.message : t('settings:deleteDialog.error'), 'error');
    }
  };

  // Formatiere Datum
  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return t('settings:apiKeys.never');
    try {
      return formatDate(dateString);
    } catch {
      return t('settings:apiKeys.invalid');
    }
  };

  if (loading) {
    return (
      <SectionWrapper>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <CircularProgress />
        </Box>
      </SectionWrapper>
    );
  }

  if (error) {
    return (
      <SectionWrapper>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper>
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 6.25 }}>
          <Typography variant="h4" sx={{ 
            color: 'text.primary', 
            mb: 1,
            fontWeight: 400,
            fontSize: '2rem'
          }}>
            {t('settings:apiKeys.pageTitle')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            data-testid="api-keys-create-button"
            sx={{ 
              textTransform: 'none',
              px: 3
            }}
          >
            {t('settings:apiKeys.createKey')}
          </Button>
        </Box>

            {/* Beschreibung */}
            <Card sx={{ 
              ...minimalCard,
              bgcolor: 'background.paper',
              mb: 3 
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '1.125rem',
                  mb: 1
                }}>
                  {t('settings:apiKeys.manageAccess')}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  mb: 2
                }}>
                  {t('settings:apiKeys.accessDescription')}{' '}
                  <a 
                    href="/docs/api" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#2563eb',
                      textDecoration: 'none'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {t('settings:apiKeys.here')}
                  </a>.
                </Typography>

              </CardContent>
            </Card>

            {/* API-Keys Tabelle */}
            <Card sx={{ 
              ...minimalCard,
              bgcolor: 'background.paper'
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  p: 3,
                  borderBottom: '1px solid rgb(0 0 0 / 0.05)'
                }}>
                  <Typography variant="h6" sx={{ 
                    color: 'text.primary',
                    fontWeight: 500,
                    fontSize: '1.125rem'
                  }}>
                    {t('settings:apiKeys.activeKeys')} ({apiKeys.length})
                  </Typography>
                </Box>
                
                {apiKeys.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      fontSize: '1rem',
                      mb: 1
                    }}>
                      {t('settings:apiKeys.noKeys')}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}>
                      {t('settings:apiKeys.noKeysHint')}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.keyName')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.key')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.status')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.createdBy')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.createdAt')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.expiresAt')}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.lastUsed')}
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '0.875rem'
                          }}>
                            {t('settings:apiKeys.actions')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apiKeys.map((apiKey) => (
                          <TableRow key={apiKey.id} hover sx={{ 
                            height: '4.25rem',
                            cursor: 'pointer'
                          }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}>
                                {apiKey.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                fontFamily: 'monospace',
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}>
                                {apiKey.keyPreview}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.75rem'
                              }}>
                                {t('settings:apiKeys.fullKeyNotAvailable')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={apiKey.isActive ? t('settings:apiKeys.active') : t('settings:apiKeys.inactive')}
                                color={apiKey.isActive ? 'success' : 'error'}
                                size="small"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}>
                                {apiKey.createdBy.name}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.75rem'
                              }}>
                                {apiKey.createdBy.email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.875rem'
                              }}>
                                {formatDateTime(apiKey.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.875rem'
                              }}>
                                {formatDateTime(apiKey.expiresAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.875rem'
                              }}>
                                {formatDateTime(apiKey.lastUsedAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={t('settings:apiKeys.deleteKey')}>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteKey(apiKey.id, apiKey.name)}
                                  size="small"
                                  data-testid={`api-keys-delete-${apiKey.id}-button`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Dialog: Neuen API-Key erstellen */}
            <Dialog 
              open={createDialogOpen} 
              onClose={() => setCreateDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }
              }}
            >
              <DialogTitle sx={{ 
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'text.primary',
                pb: 2
              }}>
                {t('settings:createDialog.title')}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 1 }}>
                  <TextField
                    autoFocus
                    label={t('settings:createDialog.nameLabel')}
                    fullWidth
                    variant="outlined"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder={t('settings:createDialog.namePlaceholder')}
                    data-testid="api-keys-create-name-input"
                    sx={{ mb: 3 }}
                    helperText={t('settings:createDialog.nameHelperText')}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('settings:createDialog.expiryLabel')}</InputLabel>
                    <Select
                      value={expirationDays}
                      label={t('settings:createDialog.expiryLabel')}
                      onChange={(e) => setExpirationDays(e.target.value as number | '')}
                      data-testid="api-keys-create-expiration-select"
                    >
                      <MenuItem value="">{t('settings:createDialog.neverExpires')}</MenuItem>
                      <MenuItem value={30}>{t('settings:createDialog.days30')}</MenuItem>
                      <MenuItem value={90}>{t('settings:createDialog.days90')}</MenuItem>
                      <MenuItem value={180}>{t('settings:createDialog.days180')}</MenuItem>
                      <MenuItem value={365}>{t('settings:createDialog.days365')}</MenuItem>
                    </Select>
                  </FormControl>

                  <Alert severity="warning" sx={{ 
                  mt: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}>
                    <strong>{t('settings:createDialog.warning')}:</strong> {t('settings:createDialog.warningText')}
                  </Alert>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button 
                  onClick={() => setCreateDialogOpen(false)}
                  disabled={creating}
                  sx={{ textTransform: 'none' }}
                >
                  {t('settings:createDialog.cancel')}
                </Button>
                <Button 
                  onClick={handleCreateKey}
                  variant="contained"
                  disabled={creating || !newKeyName.trim()}
                  data-testid="api-keys-create-submit-button"
                  sx={{ textTransform: 'none' }}
                >
                  {creating ? <CircularProgress size={20} /> : t('settings:createDialog.createButton')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Dialog: Neuer API-Key wurde erstellt */}
            <Dialog 
              open={showNewKeyDialog} 
              onClose={handleCloseNewKeyDialog}
              maxWidth="md"
              fullWidth
              disableEscapeKeyDown
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }
              }}
            >
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="success" />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.125rem'
                  }}>
                    {t('settings:newKeyDialog.title')}
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ 
                  mb: 3,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}>
                  <strong>{t('settings:newKeyDialog.warning')}:</strong> {t('settings:newKeyDialog.warningText')}
                </Alert>
                
                {newlyCreatedKey && (
                  <Box>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      mb: 0.5
                    }}>
                      {t('settings:newKeyDialog.name')}: <strong>{newlyCreatedKey.name}</strong>
                    </Typography>
                    {newlyCreatedKey.expiresAt && (
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        mb: 2
                      }}>
                        {t('settings:newKeyDialog.expiresAt')}: <strong>{formatDateTime(newlyCreatedKey.expiresAt)}</strong>
                      </Typography>
                    )}
                    
                    <Paper sx={{ 
                      p: 2, 
                      mt: 2, 
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        mb: 1
                      }}>
                        {t('settings:newKeyDialog.yourKey')}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1,
                        backgroundColor: 'background.paper',
                        border: '1px solid #d1d5db',
                        borderRadius: 0.5
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            wordBreak: 'break-all',
                            flex: 1,
                            fontSize: '0.875rem',
                            color: 'text.primary'
                          }}
                        >
                          {newlyCreatedKey.key}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={keyWasCopied ? <CheckIcon /> : <ContentCopyIcon />}
                          onClick={handleCopyNewKey}
                          color={keyWasCopied ? 'success' : 'primary'}
                          data-testid="api-keys-copy-new-key-button"
                          sx={{ textTransform: 'none' }}
                        >
                          {keyWasCopied ? t('settings:newKeyDialog.copied') : t('settings:newKeyDialog.copy')}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button 
                  onClick={handleCloseNewKeyDialog}
                  variant="contained"
                  color={keyWasCopied ? 'success' : 'warning'}
                  data-testid="api-keys-new-key-dialog-close-button"
                  sx={{ textTransform: 'none' }}
                >
                  {keyWasCopied ? t('settings:newKeyDialog.done') : t('settings:newKeyDialog.savedKey')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar für Nachrichten */}
            {/* The NotificationContext handles its own snackbar */}
        </Box>
    </SectionWrapper>
  );
};

export default ApiKeysPage;
